import { LoaderCircle } from 'lucide-react';
import type React from 'react';
import { cn } from '@/lib/utils';

interface SpinnerProps {
	/**
	 * Size of the spinner. Can be a predefined size or a custom size string.
	 * @default 'md'
	 */
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string;
	/**
	 * Custom className to apply to the spinner
	 */
	className?: string;
	/**
	 * Text color variant. Uses Tailwind color classes.
	 * @default 'text-primary'
	 */
	variant?: 'primary' | 'current' | 'white' | 'muted';
	/**
	 * Whether the spinner should be visible
	 * @default true
	 */
	visible?: boolean;
	/**
	 * Accessibility label for screen readers
	 */
	'aria-label'?: string;
}

const sizeMap = {
	xs: 'w-3 h-3',
	sm: 'w-4 h-4',
	md: 'w-6 h-6',
	lg: 'w-8 h-8',
	xl: 'w-12 h-12'
};

const variantMap = {
	primary: 'text-primary',
	current: 'text-current',
	white: 'text-white',
	muted: 'text-muted-foreground'
};

/**
 * Unified Spinner component using lucide-react LoaderCircle icon.
 * Replaces all custom CSS border spinners and inconsistent Loader icons.
 */
const Spinner = ({
	size = 'md',
	className,
	variant = 'primary',
	visible = true,
	'aria-label': ariaLabel = 'Loading'
}: SpinnerProps): React.JSX.Element | null => {
	if (!visible) return null;

	const sizeClass =
		typeof size === 'string' && sizeMap[size as keyof typeof sizeMap]
			? sizeMap[size as keyof typeof sizeMap]
			: typeof size === 'string'
				? size // Custom size string
				: sizeMap.md;

	const variantClass = variantMap[variant];

	return (
		<LoaderCircle
			className={cn('animate-spin', sizeClass, variantClass, className)}
			strokeWidth={3}
			role="status"
			aria-label={ariaLabel}
			aria-hidden="false"
		/>
	);
};

export default Spinner;
